import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

const FAIR_USE_TOAST_ID = 'fair-use-soft-limit';
let bannerShown = false;

export function isFairUseSoftLimit(error: unknown): error is AxiosError {
  if (!(error instanceof Error) || !('isAxiosError' in error)) return false;
  const ax = error as AxiosError;
  return (
    ax.response?.status === 429 &&
    Boolean(ax.response?.headers?.['x-fair-use-soft-limit'])
  );
}

const isFairUse429 = isFairUseSoftLimit;

function parseRetryAfter(error: AxiosError): number {
  const header = error.response?.headers?.['retry-after'];
  const seconds = header ? parseInt(String(header), 10) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 60;
}

function showBanner(): void {
  if (bannerShown) return;
  bannerShown = true;
  toast('Fair-use soft limit reached — uploads will continue, just slower.', {
    id: FAIR_USE_TOAST_ID,
    icon: '⏳',
    duration: 8000
  });
  setTimeout(() => {
    bannerShown = false;
  }, 60_000);
}

/**
 * Run `fn` and, if the server responds with a fair-use 429, wait the
 * server-advised Retry-After and retry. Other errors propagate.
 */
export async function withFairUseRetry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3
): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (error) {
      attempt += 1;
      if (!isFairUse429(error) || attempt >= maxAttempts) throw error;
      showBanner();
      const delaySeconds = parseRetryAfter(error);
      await new Promise(resolve => setTimeout(resolve, delaySeconds * 1000));
    }
  }
}
